/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo } from 'react';
import {
  PublicClientApplication,
  InteractionType,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  MsalAuthenticationProps,
  MsalAuthenticationTemplate,
} from '@azure/msal-react';
import { getDomainHint, isHosted, SsaEnabledHelperClass } from '../utils';
import LoginProvider from './LoginProvider';
import { LoginStoreProvider, CreateLoginStoreType } from '../store';
import UserProvider from './UserProvider';
import { getLoginHint } from '../utils/getDomainHint';

export interface AuthMethodProviderProps extends CreateLoginStoreType {
  /**
   * Component to render when autthentication success.
   */
  children?: React.ReactNode;
  /**
   * Component to render when an error occurs.
   */
  errorComponent?: MsalAuthenticationProps['errorComponent'];
  /**
   * Component to render when loading.
   */
  loadingComponent?: MsalAuthenticationProps['loadingComponent'];
  /**
   * Msal instance to use in the app. This msalInstance need to be initialized with the configuration before pass it to this component. In order to get a initialized msal instance you need to use in your app start up the method `createMsalInstance`
   */
  msalInstance?: PublicClientApplication;
  /**
   * Identity provider name to use in the app. This is used to set the domain hint in the authentication request.
   * This is optional and if not provided it will be set to the value of the `idp` query parameter in the URL.
   */
  domainHint?: string;
  /**
   * User name to use in the app. This is used to set the login hint in the authentication request.
   */
  loginHint?: string;
  /**
   * Flag that enables the use of Server Side Authentication (SSA) in the app. Currently only supported when using a .NET app as the host
   */
  useSsa?: boolean;
}

/** *
 * Component used to set the Authentication provider to use in the app
 * when the app is hosted in CX it will get the token from an endpoint in CX
 * if we are using local development we are able to use msaljs to get a token
 * to make request to the cx-api
 * @param children
 * @param clientId
 * @param redirectUri
 * @param authorityUrl
 * @param knownAuthorities
 * @param scopes
 * @constructor
 */
function AuthMethodProvider({
  children,
  apiUrl = null,
  getUserInfo = null,
  errorComponent = null,
  loadingComponent = null,
  domainHint = null,
  loginHint = null,
  useSsa = false,
  msalInstance,
}: AuthMethodProviderProps) {
  const [isSsaInitialized, setIsSsaInitialized] = React.useState(false);

  const isHostedInCx = isHosted();
  const localLoginHint = useMemo(
    () => loginHint || getLoginHint(),
    [loginHint]
  );
  const localDomainHint = useMemo(
    () => domainHint || getDomainHint(),
    [domainHint]
  );

  const authenticationRequest: RedirectRequest = useMemo(
    () => ({
      domainHint: localDomainHint,
      loginHint: localLoginHint,
      // global variable that is set in when createMsalInstance is called.
      scopes: window.scopesRef.current,
      authority: msalInstance.getConfiguration().auth.authority,
    }),
    [localLoginHint, localDomainHint]
  );

  useEffect(() => {
    window.authenticationRequest = { current: authenticationRequest };
  }, [authenticationRequest]);

  useEffect(() => {
    // We have to be sure that the SSA is initialized before rendering the app
    // so the correct credential is used in the request helper
    if (useSsa) {
      SsaEnabledHelperClass.setIsSsaEnabled(useSsa);
      setIsSsaInitialized(true);
    }
  }, []);

  if (useSsa) {
    return isSsaInitialized ? (
      <LoginStoreProvider apiUrl={apiUrl} getUserInfo={getUserInfo}>
        <UserProvider>{children}</UserProvider>
      </LoginStoreProvider>
    ) : (
      loadingComponent
    );
  }

  if (isHostedInCx)
    return (
      <LoginStoreProvider apiUrl={apiUrl} getUserInfo={getUserInfo}>
        <UserProvider>{children}</UserProvider>
      </LoginStoreProvider>
    );

  return (
    <LoginProvider msalInstance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authenticationRequest}
        errorComponent={errorComponent}
        loadingComponent={loadingComponent}
      >
        <LoginStoreProvider apiUrl={apiUrl} getUserInfo={getUserInfo}>
          <UserProvider>{children}</UserProvider>
        </LoginStoreProvider>
      </MsalAuthenticationTemplate>
    </LoginProvider>
  );
}

export default AuthMethodProvider;
